import { Injectable } from '@angular/core';
import { ReportResponseTypes } from '@mode/shared/data-access-webapp';
import { BreadcrumbTypes, ChartTypes, FormsResponse, ReportTypes, SpaceResource } from '@mode/shared/contract-common';
import { ReportDomain } from '@mode/shared/contract-common';
import { Store } from '@ngrx/store';
import { ReportResource } from '@mode/shared/data-access-webapp';
import { Observable } from 'rxjs';
import { filter, shareReplay } from 'rxjs/operators';
import { isPresent } from '@mode/shared/util-js';
import * as ReportActions from './report/report.actions';
import {
  selectReport,
  selectReportIsComplete,
  selectReportIsLoading,
  selectReportDisplayName,
  isReportLayoutEmpty,
  selectReportIFrameSrc,
  getLayout,
  selectReportTheme,
  selectReportCreatorName,
  chartTokensInLayout,
  layoutGridWithVizTokens,
  getDatasetBreadCrumbs,
  getEmpytReportBreadcrumbs,
  headerReportData,
  headerReportToken,
  displayNameForHeader,
  isPersonalCollection,
} from './report/report.selectors';
import { Encoding } from '@mode-vegas/vegas';
import { createReportAndChartFromDatasetBrowserSearch } from './report/report.actions';

@Injectable({
  providedIn: 'root',
})
export class ReportFacade {
  // root report observables
  report$: Observable<ReportDomain.Report | null>;
  reportIsLoading$: Observable<boolean>;
  reportIsComplete$: Observable<boolean>;

  // report theme/layout observables
  isReportLayoutEmpty$: Observable<boolean>;
  reportIFrameSrc$: Observable<string>;
  reportThemeNoEmpty$: Observable<ReportTypes.ReportTheme>;
  reportLayoutNoEmpty$: Observable<string>;
  chartTokensInLayout$ = this.store.select(chartTokensInLayout).pipe(filter(isPresent), shareReplay(1));
  layoutGridWithVizTokens$ = this.store.select(layoutGridWithVizTokens).pipe(filter(isPresent), shareReplay(1));

  // report info observables
  displayName$: Observable<string>;
  emptyReportBreadCrumbs$: Observable<BreadcrumbTypes.Breadcrumb[]>;
  reportCreatorName$: Observable<string | undefined>;
  datasetBreadcrumbs$: Observable<BreadcrumbTypes.Breadcrumb[]>;
  headerReportData$: Observable<ReportResource | null>;
  headerReportToken$: Observable<string>;
  displayNameForHeader$ = this.store.select(displayNameForHeader);
  isPersonalCollection$: Observable<boolean>;

  constructor(private store: Store) {
    this.report$ = store.select(selectReport);
    this.reportIsLoading$ = store.select(selectReportIsLoading);
    this.reportIsComplete$ = store.select(selectReportIsComplete);
    this.isReportLayoutEmpty$ = store.select(isReportLayoutEmpty).pipe(filter(isPresent));
    this.reportIFrameSrc$ = store.select(selectReportIFrameSrc).pipe(filter(isPresent));
    this.reportLayoutNoEmpty$ = store.select(getLayout).pipe(filter(isPresent));
    this.reportThemeNoEmpty$ = store.select(selectReportTheme).pipe(filter(isPresent));
    this.displayName$ = store.select(selectReportDisplayName);
    this.datasetBreadcrumbs$ = store.select(getDatasetBreadCrumbs);
    this.emptyReportBreadCrumbs$ = store.select(getEmpytReportBreadcrumbs);
    this.reportCreatorName$ = store.select(selectReportCreatorName);
    this.headerReportData$ = store.select(headerReportData);
    this.headerReportToken$ = store.select(headerReportToken).pipe(filter(isPresent));
    this.isPersonalCollection$ = store.select(isPersonalCollection);
  }

  loadReportWithQueries(accountId: string, reportToken: string): void {
    // defer to queries facade to load queries
    // Load local switch views from report facade? Maybe switch-view facade
    // Load imported switch views from report facade? Maybe switch-view facade
  }

  loadLegacyNewReport(report: ReportResponseTypes.ReportResource) {
    this.store.dispatch(ReportActions.editorRouteLoadNewReport({ report }));
  }

  editorRouteLoadReport(username: string, token: string) {
    this.store.dispatch(ReportActions.editorRouteLoadReport({ token, username }));
  }

  editorDialogRenameReport(token: string, name: string, description: string) {
    this.store.dispatch(ReportActions.editorDialogRenameReport({ token, name, description }));
  }

  reportViewRouteLoadReport(username: string, token: string) {
    this.store.dispatch(ReportActions.reportViewRouteLoadReport({ token, username }));
  }

  datasetViewRouteLoadReport(username: string, token: string) {
    this.store.dispatch(ReportActions.datasetViewRouteLoadReport({ token, username }));
  }

  createReportAndChartFromDatasetEditor(
    accountId: string,
    switchViewToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    fieldFormats: ChartTypes.FieldFormatMap
  ) {
    this.store.dispatch(
      ReportActions.createReportAndChartFromDatasetEditor({
        accountId,
        switchViewToken,
        chartColorPaletteToken,
        encoding,
        format,
        fieldFormats,
      })
    );
  }

  createReportAndChartFromDatasetView(
    accountId: string,
    switchViewToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    fieldFormats: ChartTypes.FieldFormatMap
  ) {
    this.store.dispatch(
      ReportActions.createReportAndChartFromDatasetView({
        accountId,
        switchViewToken,
        chartColorPaletteToken,
        encoding,
        format,
        fieldFormats,
      })
    );
  }

  createReportAndChartFromDatasetBrowserSearch(
    accountId: string,
    switchViewToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    fieldFormats: ChartTypes.FieldFormatMap
  ) {
    this.store.dispatch(
      ReportActions.createReportAndChartFromDatasetBrowserSearch({
        accountId,
        switchViewToken,
        chartColorPaletteToken,
        encoding,
        format,
        fieldFormats,
      })
    );
  }

  createReportAndChartFromDatasetBrowserUrl(
    accountId: string,
    switchViewToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    fieldFormats: ChartTypes.FieldFormatMap
  ) {
    this.store.dispatch(
      ReportActions.createReportAndChartFromDatasetBrowserUrl({
        accountId,
        switchViewToken,
        chartColorPaletteToken,
        encoding,
        format,
        fieldFormats,
      })
    );
  }

  loadWorkspaceHeaderData(token: string) {
    this.store.dispatch(ReportActions.loadWorkspaceHeaderData({ token }));
  }

  loadReportViewContainerHeaderData(token: string) {
    this.store.dispatch(ReportActions.loadReportViewContainerHeaderData({ token }));
  }

  loadRestrictedReportViewContainerHeaderData() {
    this.store.dispatch(ReportActions.loadRestrictedReportViewContainerHeaderData());
  }

  /*
  This is not good practice and will strongly advise against this method. However, in an effort to reduce the size of the header request,
  we have encountered a bug where the move space modal does not update accordingly when moved to a new collection. By adding this action,
  this will correctly update the store without making an api call. Again this practice is NOT advised and is only done to be backwards compatible
  and to not mess with legacy code that has major impact elsewhere in the application.
  */
  updateHeaderReportDataManually(response: ReportResource) {
    this.store.dispatch(ReportActions.updateHeaderReportDataManually({ headerReportData: response }));
  }

  updateHeaderSpaceDataManually(space: SpaceResource) {
    this.store.dispatch(ReportActions.updateHeaderSpaceDataManually({ space }));
  }

  updateHeaderReportSharedState(shared: boolean, forms: FormsResponse) {
    this.store.dispatch(ReportActions.updateHeaderReportSharedState({ shared, forms }));
  }

  loadDatasetWorkspaceHeaderData(token: string) {
    this.store.dispatch(ReportActions.loadDatasetWorkspaceHeaderData({ token }));
  }

  updateReportAfterDatasetPublish(name: string, publishedAt: string) {
    this.store.dispatch(ReportActions.publishDatasetSuccess({ name, publishedAt }));
  }

  submitUseDatasetAnalytics(
    runMethodType: string,
    analyticEventName: string,
    location: string,
    actionType: string,
    context: string,
    context_id: string
  ) {
    this.store.dispatch(
      ReportActions.submitUseDatasetAnalytics({
        runMethodType,
        analyticEventName,
        location,
        actionType,
        context,
        context_id,
      })
    );
  }

  // updateReport()

  // createReport()

  // deleteReport()
}
